import React from 'react';
import MarkdownContent from './MarkdownContent';

/**
 * A2UI Component Library
 * Implements A2UI specification components for React
 */

const CURRENCY_SYMBOLS = {
  'USD': '$',
  'SGD': 'S$',
  'EUR': '€',
  'GBP': '£',
  'JPY': '¥',
  'CNY': '¥',
  'KRW': '₩',
  'HKD': 'HK$',
  'AUD': 'A$',
  'CAD': 'C$',
  'INR': '₹',
  'THB': '฿',
  'MYR': 'RM',
  'IDR': 'Rp',
  'PHP': '₱',
  'VND': '₫'
};

/**
 * currency code to currency symbol
 * @param {string} currencyCode - currency code (e.g. 'USD', 'SGD') or already a symbol string
 * @returns {string} currency symbol
 */
const getCurrencySymbol = (currencyCode) => {
  if (!currencyCode) return '';
  
  // if already a symbol string (contains common currency symbols), return directly
  if (currencyCode.includes('$') || currencyCode.includes('€') || 
      currencyCode.includes('£') || currencyCode.includes('¥') || 
      currencyCode.includes('₩') || currencyCode.includes('₹') ||
      currencyCode.includes('฿') || currencyCode.includes('₱') ||
      currencyCode.includes('₫')) {
    return currencyCode;
  }
  
  // try to get the symbol from the mapping table
  const symbol = CURRENCY_SYMBOLS[currencyCode.toUpperCase()];
  if (symbol) {
    return symbol;
  }
  
  // if not found, return the original value (maybe other format of currency code)
  return currencyCode;
};

export const A2UIText = ({ text, style, className }) => {
  if (!text) return null;
  
  return (
    <div className={`a2ui-text ${className || ''}`} style={style}>
      <MarkdownContent text={text} />
    </div>
  );
};

// Button Component
export const A2UIButton = ({ 
  label, 
  onClick, 
  variant = 'primary', 
  disabled, 
  style, 
  className, 
  id, 
  action, 
  data,
  amount,
  currency,
  type = 'button'
}) => {
  const handleClick = (e) => {
    if (onClick && !disabled) {
      // Prevent default form submission if type is 'button'
      if (type === 'button') {
        e.preventDefault();
        e.stopPropagation();
      }
      console.log('A2UIButton clicked:', id);
      onClick({ 
        type: 'button_click', 
        widgetId: id, 
        label, 
        action: action || null, 
        data: data || null,
        amount: amount != null ? amount : null,
        currency: currency || null
      });
    }
  };

  // Format amount display if amount is provided
  const formatAmountDisplay = () => {
    if (amount == null) return label;
    
    const currencySymbol = getCurrencySymbol(currency || 'USD');
    let formattedAmount;
    
    if (typeof amount === 'number') {
      // Format number with 2 decimal places
      formattedAmount = amount.toLocaleString('en-US', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
    } else {
      // If it's already a string, use it as is
      formattedAmount = amount;
    }
    
    return `${label} - ${currencySymbol}${formattedAmount}`;
  };

  return (
    <button
      id={id}
      type={type}
      className={`a2ui-button a2ui-button-${variant} ${className || ''}`}
      onClick={handleClick}
      disabled={disabled}
      style={style}
    >
      {amount != null ? formatAmountDisplay() : label}
    </button>
  );
};

// Input Component (supports text, number, email, etc.)
export const A2UIInput = ({ 
  id, 
  type = 'text', 
  placeholder, 
  value, 
  onChange, 
  label,
  required,
  min,
  max,
  step,
  style,
  className 
}) => {
  const handleChange = (e) => {
    if (onChange) {
      onChange({ 
        type: 'input_change', 
        widgetId: id, 
        value: e.target.value 
      });
    }
  };

  return (
    <div className={`a2ui-input-wrapper ${className || ''}`} style={style}>
      {label && <label htmlFor={id} className="a2ui-input-label">{label}</label>}
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        value={value || ''}
        onChange={handleChange}
        required={required}
        min={min}
        max={max}
        step={step}
        className="a2ui-input"
      />
    </div>
  );
};

// Date Input Component
export const A2UIDateInput = ({ 
  id, 
  label,
  value, 
  onChange, 
  required,
  min,
  max,
  style,
  className 
}) => {
  const handleChange = (e) => {
    if (onChange) {
      onChange({ 
        type: 'input_change', 
        widgetId: id, 
        value: e.target.value 
      });
    }
  };

  return (
    <div className={`a2ui-input-wrapper ${className || ''}`} style={style}>
      {label && <label htmlFor={id} className="a2ui-input-label">{label}</label>}
      <input
        id={id}
        type="date"
        value={value || ''}
        onChange={handleChange}
        required={required}
        min={min}
        max={max}
        className="a2ui-input a2ui-date-input"
      />
    </div>
  );
};

// Select/Dropdown Component
export const A2UISelect = ({ 
  id, 
  label,
  value, 
  onChange, 
  options = [],
  required,
  style,
  className 
}) => {
  const handleChange = (e) => {
    if (onChange) {
      onChange({ 
        type: 'input_change', 
        widgetId: id, 
        value: e.target.value 
      });
    }
  };

  // Normalize options array - handle both object and string formats
  const normalizedOptions = (options || []).map((option, index) => {
    if (typeof option === 'string') {
      return { value: option, label: option };
    }
    if (typeof option === 'object' && option !== null) {
      return {
        value: option.value !== undefined ? option.value : option.label || String(index),
        label: option.label !== undefined ? option.label : option.value || String(index)
      };
    }
    return { value: String(index), label: String(option) };
  });

  return (
    <div className={`a2ui-input-wrapper ${className || ''}`} style={style}>
      {label && <label htmlFor={id} className="a2ui-input-label">{label}</label>}
      <select
        id={id}
        value={value || ''}
        onChange={handleChange}
        required={required}
        className="a2ui-input a2ui-select"
      >
        <option value="">Please select...</option>
        {normalizedOptions.map((option, index) => (
          <option key={`${option.value}-${index}`} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

// Checkbox Component
export const A2UICheckbox = ({ 
  id, 
  label,
  checked, 
  onChange, 
  style,
  className 
}) => {
  const handleChange = (e) => {
    if (onChange) {
      onChange({ 
        type: 'input_change', 
        widgetId: id, 
        value: e.target.checked 
      });
    }
  };

  return (
    <div className={`a2ui-checkbox-wrapper ${className || ''}`} style={style}>
      <label className="a2ui-checkbox-label">
        <input
          id={id}
          type="checkbox"
          checked={checked || false}
          onChange={handleChange}
          className="a2ui-checkbox"
        />
        <span>{label}</span>
      </label>
    </div>
  );
};

// List Item Component
export const A2UIListItem = ({ item, onItemClick, style, className }) => {
  const handleClick = () => {
    if (onItemClick) {
      onItemClick({ 
        type: 'item_click', 
        itemId: item.id, 
        item 
      });
    }
  };

  // Support multiple image field names: image_url, image, imageUrl
  const imageUrl = item.image_url || item.image || item.imageUrl;

  const hasRowLayout =
    item.left_text1 ||
    item.middle_text ||
    item.left_text2 ||
    item.right_text1 ||
    item.right_text2;

  const rowLines = hasRowLayout
    ? [
        { left: item.left_text1, middle: item.middle_text, right: item.right_text1, level: 1 },
        { left: item.left_text2, right: item.right_text2, level: 2 },
      ].filter(line => line.left || line.middle || line.right)
    : [];

  // If item has a button (price != null), don't make the whole item clickable
  const hasButton = item.price != null;
  const shouldBeClickable = onItemClick && !hasButton;

  return (
    <li 
      className={`a2ui-list-item ${className || ''} ${shouldBeClickable ? 'a2ui-clickable' : ''} ${imageUrl ? 'a2ui-list-item-with-image' : ''}`}
      onClick={shouldBeClickable ? handleClick : undefined}
      style={style}
    >
      {imageUrl && (
        <div className="a2ui-list-item-image-wrapper">
          <img
            src={imageUrl}
            alt={item.title || ''}
            className="a2ui-list-item-image"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}
      <div className="a2ui-list-item-content">
          <div className="a2ui-list-item-main">
            {item.title && <div className="a2ui-list-item-title">{item.title}</div>}
            {item.subtitle && <div className="a2ui-list-item-subtitle">{item.subtitle}</div>}
          </div>
          <div className="a2ui-row-lines">
            {rowLines.map((line, idx) => (
              <div
                key={`${item.id || 'row'}_${line.level}_${idx}`}
                className={`a2ui-row-line-row a2ui-row-line-${line.level}`}
              >
                {line.left ? (
                  <div className="a2ui-row-cell a2ui-row-cell-left">{line.left}</div>
                ) : (
                  <div className="a2ui-row-cell a2ui-row-cell-left" />
                )}
                {line.middle ? (
                  <div className="a2ui-row-cell a2ui-row-cell-middle">{line.middle}</div>
                ) : (
                  <div className="a2ui-row-cell a2ui-row-cell-middle" />
                )}
                {line.right ? (
                  <div className="a2ui-row-cell a2ui-row-cell-right">{line.right}</div>
                ) : (
                  <div className="a2ui-row-cell a2ui-row-cell-right" />
                )}
              </div>
            ))}
          </div>
      
        {item.description && (
          <div className="a2ui-list-item-description">{item.description}</div>
        )}
        {item.tags && item.tags.length > 0 && (
          <div className="a2ui-list-item-tags">
            {item.tags.map((tag, idx) => (
              <span key={idx} className="a2ui-tag">
                {tag}
              </span>
            ))}
          </div>
        )}
        {item.actions && item.actions.length > 0 && (
          <div className="a2ui-list-item-actions">
            {item.actions.map((action, idx) => (
              <A2UIButton
                key={idx}
                id={`${item.id}_action_${idx}`}
                label={action.label}
                variant={action.variant || 'secondary'}
                onClick={action.onClick}
                className="a2ui-list-item-action-btn"
              />
            ))}
          </div>
        )}
      </div>
      <div className="a2ui-list-item-meta">
        {item.link && (
          <a
            className="a2ui-link-btn"
            href={item.link}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => {
              // Prevent clicking the row item when user clicks link
              e.stopPropagation?.();
            }}
          >
            View
          </a>
        )}
        {item.price != null && (
          <>
            <div className="a2ui-list-item-price a2ui-text-tertiary" style={{ textAlign: 'center', fontSize: '1.4rem', fontWeight: 700 }}>
              {getCurrencySymbol(item.currency || item.price_currency || 'USD')}{item.price}
            </div>
            <A2UIButton
              id={`${item.id}_view_room`}
              label="Select"
              variant="secondary"
              onClick={(e) => {
                e.stopPropagation?.();
                if (onItemClick) {
                  onItemClick({ 
                    type: 'item_click', 
                    itemId: item.id, 
                    item 
                  });
                }
              }}
              className="a2ui-view-room-btn"
              style={{ backgroundColor: '#E31837' , color: '#FFFFFF' }}
            />
          </>
        )}
      </div>
    </li>
  );
};

// List Component
export const A2UIList = ({ 
  id, 
  title, 
  items = [], 
  onItemClick, 
  style, 
  className 
}) => {
  if (!items || items.length === 0) return null;

  return (
    <div id={id} className={`a2ui-list ${className || ''}`} style={style}>
      {title && <h3 className="a2ui-list-title">{title}</h3>}
      <ul className="a2ui-list-items">
        {items.map((item) => (
          <A2UIListItem
            key={item.id || item.title}
            item={item}
            onItemClick={onItemClick}
          />
        ))}
      </ul>
    </div>
  );
};

// Card Item Component (internal use)
const A2UICardItem = ({ item, onCardClick, style, className }) => {
  const handleClick = () => {
    if (onCardClick) {
      onCardClick({ 
        type: 'card_click', 
        itemId: item.id, 
        item 
      });
    }
  };

  // Support multiple image field names: image_url, image, imageUrl
  const imageUrl = item.image_url || item.image || item.imageUrl;

  const hasRowLayout =
    item.left_text1 ||
    item.middle_text ||
    item.left_text2 ||
    item.right_text1 ||
    item.right_text2;

  const rowLines = hasRowLayout
    ? [
        { left: item.left_text1, middle: item.middle_text, right: item.right_text1, level: 1 },
        { left: item.left_text2, right: item.right_text2, level: 2 },
      ].filter(line => line.left || line.middle || line.right)
    : [];

  return (
    <div 
      className={`a2ui-card ${className || ''} ${onCardClick ? 'a2ui-clickable' : ''} ${rowLines.length > 0 ? 'a2ui-card-full-width' : ''}`}
      onClick={onCardClick ? handleClick : undefined}
      style={style}
    >
      {imageUrl && (
        <div className="a2ui-card-image-wrapper">
          <img
            src={imageUrl}
            alt={item.title || ''}
            className="a2ui-card-image"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}
      <div className={`a2ui-card-body ${!imageUrl ? 'a2ui-card-body-fullwidth' : ''}`} >
        {item.title && <div className="a2ui-card-title">{item.title}</div>}
        {item.subtitle && (
          <div className="a2ui-card-subtitle">{item.subtitle}</div>
        )}
        <div className="a2ui-row-lines a2ui-row-lines-card">
          {rowLines.map((line, idx) => (
            <div
              key={`${item.id || 'row'}_${line.level}_${idx}`}
              className={`a2ui-row-line-row a2ui-row-line-${line.level}`}
            >
              {line.left ? (
                <div className="a2ui-row-cell a2ui-row-cell-left">{line.left}</div>
              ) : (
                <div className="a2ui-row-cell a2ui-row-cell-left" />
              )}
              {line.middle ? (
                <div className="a2ui-row-cell a2ui-row-cell-middle">{line.middle}</div>
              ) : (
                <div className="a2ui-row-cell a2ui-row-cell-middle" />
              )}
              {line.right ? (
                <div className="a2ui-row-cell a2ui-row-cell-right">{line.right}</div>
              ) : (
                <div className="a2ui-row-cell a2ui-row-cell-right" />
              )}
            </div>
          ))}
        </div>
        <div className="a2ui-card-footer">
          {item.price != null && (
            <span className="a2ui-card-price">
              {getCurrencySymbol(item.currency || item.price_currency || 'USD')}{item.price}
            </span>
          )}
        </div>
        {item.actions && item.actions.length > 0 && (
          <div className="a2ui-card-actions">
            {item.actions.map((action, idx) => (
              <A2UIButton
                key={idx}
                id={`${item.id}_action_${idx}`}
                label={action.label}
                variant={action.variant || 'primary'}
                onClick={action.onClick}
                className="a2ui-card-action-btn"
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Card Container Component
export const A2UICard = ({ 
  id, 
  title, 
  items = [], 
  onCardClick, 
  style, 
  className 
}) => {
  if (!items || items.length === 0) return null;

  return (
    <div id={id} className={`a2ui-card-container ${className || ''}`} style={style}>
      {title && <h3 className="a2ui-card-title">{title}</h3>}
      <div className="a2ui-card-items">
        {items.map((item) => (
          <A2UICardItem
            key={item.id || item.title}
            item={item}
            onCardClick={onCardClick}
          />
        ))}
      </div>
    </div>
  );
};

// Carousel Component
export const A2UICarousel = ({ 
  id, 
  title, 
  items = [], 
  onCardClick, 
  style, 
  className 
}) => {
  if (!items || items.length === 0) return null;

  return (
    <div id={id} className={`a2ui-carousel ${className || ''}`} style={style}>
      {title && <h3 className="a2ui-carousel-title">{title}</h3>}
      <div className="a2ui-carousel-container">
        {items.map((item) => (
          <A2UICardItem
            key={item.id || item.title}
            item={item}
            onCardClick={onCardClick}
            className="a2ui-carousel-card"
          />
        ))}
      </div>
    </div>
  );
};

// Form Component (supports multiple field types)
// Supports both fields prop (legacy) and children (for streaming add operations)
export const A2UIForm = ({ 
  id, 
  title, 
  fields = [], 
  onSubmit, 
  submitLabel = 'Submit',
  style,
  className,
  children
}) => {
  const [formData, setFormData] = React.useState({});
  const formRef = React.useRef(null);

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) {
      let collectedData = {};
      
      // Collect data from form elements (works for both fields prop and children)
      if (formRef.current) {
        const formElement = formRef.current;
        
        // Method 1: Use FormData API (requires name attribute)
        const formDataObj = new FormData(formElement);
        for (const [key, value] of formDataObj.entries()) {
          collectedData[key] = value;
        }
        
        // Method 2: Directly query all form inputs by id (fallback)
        const inputs = formElement.querySelectorAll('input, select, textarea');
        inputs.forEach(input => {
          if (input.id && input.id !== `${id}_submit`) {
            if (input.type === 'checkbox') {
              collectedData[input.id] = input.checked;
            } else {
              collectedData[input.id] = input.value || '';
            }
          }
        });
      }
      
      // Merge with formData state (for fields prop mode)
      collectedData = { ...formData, ...collectedData };
      
      onSubmit({ 
        type: 'form_submit', 
        formId: id, 
        data: collectedData 
      });
    }
  };

  const renderField = (field) => {
    const fieldType = (field.type || 'text').toLowerCase();
    const commonProps = {
      id: field.id,
      label: field.label,
      required: field.required,
      value: formData[field.id],
      onChange: (event) => handleFieldChange(field.id, event.value),
      className: field.className
    };

    switch (fieldType) {
      case 'date':
        return (
          <A2UIDateInput
            key={field.id}
            {...commonProps}
            min={field.min}
            max={field.max}
          />
        );
      
      case 'select':
      case 'dropdown':
        return (
          <A2UISelect
            key={field.id}
            {...commonProps}
            options={field.options || []}
          />
        );
      
      case 'checkbox':
        return (
          <A2UICheckbox
            key={field.id}
            {...commonProps}
            checked={formData[field.id] || false}
          />
        );
      
      case 'number':
        return (
          <A2UIInput
            key={field.id}
            {...commonProps}
            type="number"
            min={field.min}
            max={field.max}
            step={field.step}
            placeholder={field.placeholder}
          />
        );
      
      default:
        return (
          <A2UIInput
            key={field.id}
            {...commonProps}
            type={field.type || 'text'}
            placeholder={field.placeholder}
          />
        );
    }
  };

  // Support both fields prop and children
  // If children exist (and are React elements), render them; otherwise use fields prop
  const hasChildren = children && (React.Children.count(children) > 0 || (Array.isArray(children) && children.length > 0));
  const hasFields = fields && fields.length > 0;

  // Wrap children with onChange handlers to update formData
  // Also add name attribute for FormData collection
  const childrenWithHandlers = React.useMemo(() => {
    if (!hasChildren || hasFields) return children;
    
    return React.Children.map(children, (child) => {
      if (React.isValidElement(child) && child.props.id) {
        const fieldId = child.props.id;
        const originalOnChange = child.props.onChange;
        
        return React.cloneElement(child, {
          ...child.props,
          // Add name attribute for FormData collection
          name: fieldId,
          onChange: (event) => {
            // Extract value from event
            let value;
            if (event?.value !== undefined) {
              value = event.value;
            } else if (event?.target) {
              value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
            } else {
              value = event;
            }
            
            // Update formData when child component changes
            handleFieldChange(fieldId, value);
            
            // Also call original onChange if exists (for A2UIRenderer's handleEvent)
            if (originalOnChange) {
              originalOnChange(event);
            }
          }
        });
      }
      return child;
    });
  }, [children, hasChildren, hasFields]);

  return (
    <form 
      ref={formRef}
      id={id} 
      className={`a2ui-form ${className || ''}`} 
      onSubmit={handleSubmit}
      style={style}
    >
      {title && <h3 className="a2ui-form-title">{title}</h3>}
      <div className="a2ui-form-fields">
        {hasChildren && !hasFields ? childrenWithHandlers : fields.map(field => renderField(field))}
      </div>
      <div className="a2ui-form-actions">
        <A2UIButton
          id={`${id}_submit`}
          label={submitLabel}
          type="submit"
          variant="primary"
        />
      </div>
    </form>
  );
};

// Container Component
export const A2UIContainer = ({ children, style, className, id }) => {
  return (
    <div id={id} className={`a2ui-container ${className || ''}`} style={style}>
      {children}
    </div>
  );
};

